import type { HTMLProps, ReactElement, ReactNode } from 'react';
import cx from 'clsx';
import {
  IconCheckmarkRoundedFilledSmall,
  IconClockFilledSmall,
  IconCrossRoundedFilledSmall,
  IconErrorFilledSmall,
  IconProcessingFilledSmall,
  IconWarningSignFilledSmall,
} from '@repo/monochrome-icons';
import styles from './status.module.css';

export const STATUS_TYPES = [
  'inactive',
  'error',
  'in-progress',
  'validated',
  'waiting',
  'warning',
  'canceled',
] as const;

export type StatusType = (typeof STATUS_TYPES)[number];

export const isStatusType = (value: string): value is StatusType => {
  return STATUS_TYPES.some(statusType => statusType === value);
};

export interface StatusProps extends HTMLProps<HTMLSpanElement> {
  level?: StatusType;
  text: ReactNode;
  className?: string;
}

const ICONS = {
  error: <IconErrorFilledSmall />,
  inactive: null,
  'in-progress': <IconProcessingFilledSmall />,
  validated: <IconCheckmarkRoundedFilledSmall />,
  waiting: <IconClockFilledSmall />,
  warning: <IconWarningSignFilledSmall />,
  canceled: <IconCrossRoundedFilledSmall />,
} as const;

export function BadgeStatus({
  level = 'waiting',
  text,
  className,
  ...props
}: StatusProps): ReactElement {
  return (
    <span
      className={cx('caption', styles.status, className)}
      data-test-badge-status=""
      data-test-level={level}
      {...props}
    >
      {Boolean(ICONS[level]) && (
        <span className={cx(styles.icon, styles[level])}>{ICONS[level]}</span>
      )}
      {text}
    </span>
  );
}
